/**
 * ScoringEngine - Multi-criteria scoring for all activity types.
 *
 * Extends bumblebee StarRatingCalculator with support for different
 * scoring models per activity type.
 *
 * @see GitHub Issue #852
 */

export default class ScoringEngine {
    /**
     * Calculate star rating for a challenge activity (ported from bumblebee).
     * Based on block efficiency vs optimal solution.
     *
     * @param {number} usedBlocks - Blocks the child used
     * @param {number} optimalBlocks - Minimum blocks needed
     * @returns {number} 1-3 stars
     */
    static calculateChallengeStars (usedBlocks, optimalBlocks) {
        if (optimalBlocks <= 0) return 1;
        const efficiency = optimalBlocks / usedBlocks;
        if (efficiency >= 0.9) return 3;
        if (efficiency >= 0.7) return 2;
        return 1;
    }

    /**
     * Calculate star rating for a guided creation activity.
     * Based on step completion + bonus criteria.
     *
     * @param {object} result - Evaluation result
     * @param {number} result.stepsCompleted - Number of guided steps completed
     * @param {number} result.totalSteps - Total guided steps
     * @param {Array} result.bonusesEarned - Array of bonus criteria met
     * @param {object} successCriteria - From activity definition
     * @returns {number} 1-3 stars
     */
    static calculateGuidedCreationStars (result, successCriteria) {
        let stars = 0;

        // Base star: complete all guided steps
        if (result.stepsCompleted >= (successCriteria.requiredSteps || result.totalSteps)) {
            stars = 1;
        }

        // Bonus stars from criteria
        if (result.bonusesEarned && Array.isArray(successCriteria.bonusCriteria)) {
            for (const bonus of successCriteria.bonusCriteria) {
                if (result.bonusesEarned.includes(bonus.name)) {
                    stars += (bonus.starBonus || 1);
                }
            }
        }

        return Math.min(Math.max(stars, 0), 3);
    }

    /**
     * Calculate star rating for a free creation activity.
     * Based on rubric criteria met.
     *
     * @param {Array} criteriaResults - [{name, met: boolean}]
     * @param {Array} rubric - Rubric from success criteria
     * @returns {number} 1-3 stars
     */
    static calculateFreeCreateStars (criteriaResults, rubric) {
        if (!criteriaResults || !rubric) return 1;
        const met = criteriaResults.filter(r => r.met).length;
        const total = rubric.length;
        if (total === 0) return 1;

        const ratio = met / total;
        if (ratio >= 0.9) return 3;
        if (ratio >= 0.6) return 2;
        return 1;
    }

    /**
     * Universal scoring: dispatch to the correct calculator based on activity type.
     *
     * @param {string} activityType
     * @param {object} evaluationResult
     * @param {object} successCriteria
     * @returns {number} 1-3 stars
     */
    static calculateStars (activityType, evaluationResult, successCriteria) {
        switch (activityType) {
        case 'challenge':
            return ScoringEngine.calculateChallengeStars(
                evaluationResult.usedBlocks,
                successCriteria.optimalBlocks
            );
        case 'guided-creation':
            return ScoringEngine.calculateGuidedCreationStars(evaluationResult, successCriteria);
        case 'free-create':
            return ScoringEngine.calculateFreeCreateStars(evaluationResult.criteriaResults, successCriteria.rubric);
        case 'explore':
            return ScoringEngine.calculateExploreStars(evaluationResult, successCriteria);
        default:
            return 1;
        }
    }

    /**
     * Calculate stars for explore activities.
     * @param {object} result - {discoveriesFound: number}
     * @param {object} criteria - {discoveries: Array}
     * @returns {number}
     */
    static calculateExploreStars (result, criteria) {
        if (!criteria.discoveries || criteria.discoveries.length === 0) return 1;
        const ratio = (result.discoveriesFound || 0) / criteria.discoveries.length;
        if (ratio >= 0.9) return 3;
        if (ratio >= 0.6) return 2;
        return 1;
    }

    /**
     * Check if a new rating improves on the current best.
     * @param {number} newStars
     * @param {number} currentBest
     * @returns {boolean}
     */
    static improves (newStars, currentBest) {
        return newStars > (currentBest || 0);
    }
}
